import { createAsyncThunk, createSlice, PayloadAction, } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import { HttpStatusCode } from "axios";
import PaginatedResponse from "common/contracts/paginated-response";
import { assertIsAxiosError } from "common/errors";
import Guid from "common/values/guid/guid";
import MessageNotification from "notifications/entities/message-notification/message-notification";
import NotificationsAPIService from "notifications/entities/notification/api/notifications-api-service";
import NotificationQuery from "notifications/entities/notification/api/request-contracts/query-notifications-api-request";
import Notification from "notifications/entities/notification/notification";
import { useSelector } from "react-redux";
import Session from "users/session/session";

export type NotificationStoreState = {
  byId: {
    entries: Record<string, Notification>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<Notification | MessageNotification>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, NotificationQuery>;
  };
};

const initialState: NotificationStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateNotificationsByQuery = createAsyncThunk(
  "notifications/getNotificationsByQuery",
  async (
    { session, query }: { session: Session; query: NotificationQuery }
  ) => {
    const apiService = new NotificationsAPIService(session);
    return await apiService.getNotificationsByQuery(query);
  },
  {
    condition: (
      { query }: { session: Session; query: NotificationQuery },
      { getState }
    ) => {
      const state = getState() as RootState;
      const queryKey = query.asSearchParams().toString();
      // Only dispatch if not already loading
      return !state.notifications.byQuery.loading[queryKey];
    }
  }
);
export const populateNotificationsById = createAsyncThunk(
  "notifications/getNotificationsById",
  async ({ session, id }: { session: Session; id: Guid }, thunkAPI) => {
    try {
      const apiService = new NotificationsAPIService(session);
      return await apiService.getNotificationById(id);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound) {
        return thunkAPI.rejectWithValue("Not found.")
      }
      return thunkAPI.rejectWithValue(error.response?.data ?? JSON.stringify(error));
    }
  },
  {
    condition: (
      { id }: { session: Session; id: Guid },
      { getState }
    ) => {
      const state = getState() as RootState;
      // Only dispatch if not already loading for this id
      return !state.notifications.byId.loading[id.value];
    }
  }
);
export const markAllNotificationsAsSeen = createAsyncThunk(
  "notifications/markAllNotificationsAsSeen",
  async (
    session: Session,
    thunkAPI
  ) => {
    try {
      const apiService = new NotificationsAPIService(session);
      await apiService.markAllNotificationsAsSeen();
    } catch (error) {
      assertIsAxiosError(error);
      return thunkAPI.rejectWithValue(error.response?.data ?? JSON.stringify(error));
    }
  },
  {
    condition: (
      session: Session,
      { getState }
    ) => {
      const state = getState() as RootState;
      const userId = session.user?.id?.value;
      // Only dispatch if not already loading for this user
      // You may want a dedicated loading flag for this operation, but for now, always allow
      return userId ? !state.notifications.byId.loading[userId] : true;
    }
  }
);
const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      const id = action.payload.id?.value;
      if (!id) {
        return;
      }
      state.byId.entries[id] = action.payload;
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeNotification: (state, action: PayloadAction<Guid>) => {
      const id = action.payload.value;
      delete state.byId.entries[id];
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateNotificationInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: NotificationQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      populateNotificationsByQuery.pending,
      (state, action) => {
        if (!action.meta.arg.query?.filter) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populateNotificationsByQuery.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query?.filter) {
          return;
        }
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.loading[query] = false;
        state.byQuery.entries[query] = action.payload;
      }
    );
    builder.addCase(
      populateNotificationsByQuery.rejected,
      (state, action) => {
        if (!action.meta.arg.query?.filter) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.payload as string;
      }
    );

    builder.addCase(
      populateNotificationsById.pending,
      (state, action) => {
        if (!action.meta.arg.id) {
          return;
        }
        const id = action.meta.arg.id.value;
        state.byId.loading[id] = true;
        state.byId.error[id] = null;
      }
    );
    builder.addCase(
      populateNotificationsById.fulfilled,
      (state, action) => {
        if (!action.meta.arg.id) {
          return;
        }
        const id = action.meta.arg.id.value;
        state.byId.loading[id] = false;
        state.byId.entries[id] = action.payload;
      }
    );
    builder.addCase(
      populateNotificationsById.rejected,
      (state, action) => {
        if (!action.meta.arg.id) {
          return;
        }
        const id = action.meta.arg.id.value;
        state.byId.loading[id] = false;
        state.byId.error[id] = action.payload as string;
      }
    );
    builder.addCase(
      markAllNotificationsAsSeen.fulfilled,
      (state, action) => {
        if (!action.meta.arg.user?.id) {
          console.error("Failed to mark all notifications as seen. User ID is undefined.");
          return;
        }
        for (const [query, notificationPage] of Object.entries(state.byQuery.entries)) {
          const newData: Array<Notification | MessageNotification> = [];
          notificationPage.data.forEach((notification) => {
            notification.markSeen(action.meta.arg.user?.id);
            const plainNotification = Object.assign(
              Object.create(Object.getPrototypeOf(notification)),
              notification
            );
            newData.push(plainNotification);
          });
          state.byQuery.entries[query] = notificationPage.appendOrReplaceData(newData);
        }
      }
    );
  },
});

export const {
  addNotification,
  removeNotification,
  updateNotificationInterfaceQuery,
} = notificationsSlice.actions;

export const getNotificationsByQuery = (query: NotificationQuery) => {
  return useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    return state.notifications.byQuery.entries[
      query.asSearchParams().toString()
    ];
  });
};
export const getIsLoadingNotificationsByQuery = (query: NotificationQuery) =>
  useSelector((state: RootState) => {
    if (query === undefined) {
      return undefined;
    }
    return state.notifications.byQuery.loading[
      query.asSearchParams().toString()
    ] ?? false;
  });
export const getErrorLoadingNotificationsByQuery = (
  query: NotificationQuery
) =>
  useSelector(
    (state: RootState) => {
      if (!query) return false;
      return state.notifications.byQuery.error[query.asSearchParams().toString()] ?? false;
    }
  );

export const getNotificationById = (id: Guid) =>
  useSelector((state: RootState) => state.notifications.byId.entries[id.value]);
export const getIsLoadingNotificationById = (id: Guid) =>
  useSelector((state: RootState) => state.notifications.byId.loading[id.value]);
export const getErrorLoadingNotificationById = (id: Guid) =>
  useSelector((state: RootState) => state.notifications.byId.error[id.value]);

export const getNotificationQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.notifications.interfaceQueries.entries[interfaceName]
  );

export default notificationsSlice;
